import "@/polyfills/nodelist-foreach"
import "@/polyfills/closest"
import events from "@/bl-events"
import getLocale from "@/utils/locale"
import loadScript from "@/utils/load-script"
import assembleShareUrl from "@/utils/assemble-share-url"
import { isChrome } from "@/utils/user-agent"
import globals from "@/utils/globals"

const loginQuery = "[data-action=facebook-login]"
const shareQuery = "[data-action=share-facebook]"
let isLoaded = false

const loadFBScript = () => {
  const locale = getLocale()
  const url = `https://connect.facebook.net/${locale}_${locale.toUpperCase()}/sdk.js`
  // See https://developers.facebook.com/docs/javascript/quickstart#loading
  window.fbAsyncInit = function () {
    window.FB.init({
      appId: globals.facebookAppId,
      status: true,
      cookie: false,
      xfbml: true,
      version: "v20.0"
    })
  }
  return loadScript(url).catch(() => {
    /* ignored to reduce noise in error tracking */
  })
}

export const onLoginClick = (trigger) => {
  const form = trigger.closest("form")

  form.querySelectorAll("input[type=email],input[type=password]").forEach((node) => {
    node.value = ""
  })

  if (window.FB) {
    Promise.all([getFacebookAuthData(), isFacebookTokenValid()]).then(([authData, isTokenValid]) => {
      const submitForm = (authResponse) => {
        form.querySelector(trigger.dataset.actionUrl).value = authResponse.accessToken
        form.submit()
      }

      if (authData.status === "connected" && isTokenValid) {
        submitForm(authData.authResponse)
      } else {
        window.FB.login(
          (response) => {
            if (response.authResponse) {
              submitForm(response.authResponse)
            }
          },
          { scope: "email" }
        )
      }
    })
  } else {
    loadFBScript()
  }
}

const getFacebookAuthData = () => new Promise((resolve) => window.FB.getLoginStatus(resolve))

const isFacebookTokenValid = () =>
  new Promise((resolve) => {
    // `FB.getLoginStatus` may return invalid token (e.g. after user removed our app from Facebook settings)
    // So we need to check token for validity: https://developers.facebook.com/docs/facebook-login/guides/%20access-tokens/debugging#errors
    window.FB.api("/me", (response) => resolve(!response.error))
  })

const onShareClick = (trigger) => {
  events.track("body-20100417-share", "1-fb")

  let display = "iframe"
  let shareUrl

  if (trigger.dataset.disableLinkSharing && trigger.getAttribute("href")) {
    shareUrl = assembleShareUrl(trigger.href)
  } else {
    shareUrl = assembleShareUrl("/")
  }

  if (isChrome()) {
    display = "popup"
  }

  if (window.FB) {
    window.FB.ui(
      {
        method: "share",
        display: display,
        href: shareUrl
      },
      () => {}
    )
  }
}

const attachEvents = () => {
  const nodes = document.querySelectorAll(`${loginQuery},${shareQuery}`)
  nodes.forEach((node) => node.removeAttribute("disabled"))

  // Ensure there no old listeners
  document.removeEventListener("click", onDocClick)

  // Add the new one
  document.addEventListener("click", onDocClick)
}

const onDocClick = (e) => {
  const loginTarget = e.target.closest(loginQuery)
  if (loginTarget) {
    e.preventDefault()
    onLoginClick(loginTarget)
    return
  }

  const shareTarget = e.target.closest(shareQuery)
  if (shareTarget) {
    e.preventDefault()
    onShareClick(shareTarget)
    return
  }
}

const initFacebookHandles = () => {
  const nodes = document.querySelectorAll(`${loginQuery},${shareQuery}`)

  if (nodes.length <= 0) {
    return
  }

  if (isLoaded) {
    nodes.forEach((node) => node.removeAttribute("disabled"))
  } else {
    nodes.forEach((node) => node.setAttribute("disabled", "disabled"))

    isLoaded = true
    loadFBScript().then(attachEvents)
  }
}

if (globals.clientPlatform === "web") {
  // Listen to global event for reattaching the handlers
  events.on("facebook:init", initFacebookHandles)

  // Trigger the event initially on each page load
  initFacebookHandles()
}
